import { useEffect, useState, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { AuthContext } from '@/App';
import axios from 'axios';
import { Calendar as CalendarIcon, Upload, FileText, Edit, Trash2, AlertCircle, Plus, X, Filter, Target } from 'lucide-react';
import { toast } from 'sonner';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '@/styles/calendar.css';
import AddManualEventModal from '@/components/AddManualEventModal';
import EditSyllabusItemModal from '@/components/EditSyllabusItemModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CollegeCourseDashboard() {
  const { courseId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [syllabusData, setSyllabusData] = useState(null);
  const [smartGoals, setSmartGoals] = useState([]);
  const [manualEvents, setManualEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [filters, setFilters] = useState({
    assignment: true,
    exam: true,
    project: true,
    reading: true,
    quiz: true,
    other: true,
    study_session: true // Add filter for study sessions
  });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('month');
  
  // Create localizer inside component
  const localizer = useMemo(() => momentLocalizer(moment), []);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    loadCourseData();
  }, [user, courseId]);

  const loadCourseData = async () => {
    try {
      const [courseRes, syllabusRes, goalsRes, manualEventsRes] = await Promise.all([
        axios.get(`${API}/study-buddy/courses/${courseId}`, { withCredentials: true }),
        axios.get(`${API}/study-buddy/courses/${courseId}/syllabus`, { withCredentials: true }),
        axios.get(`${API}/study-buddy/courses/${courseId}/smart-goals`, { withCredentials: true }),
        axios.get(`${API}/study-buddy/courses/${courseId}/manual-events`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      
      setCourse(courseRes.data);
      setSyllabusData(syllabusRes.data);
      setSmartGoals(goalsRes.data);
      setManualEvents(manualEventsRes.data);
    } catch (error) {
      console.error('Failed to load course:', error);
      toast.error('Failed to load course data');
      navigate('/study-buddy');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/study-buddy/courses/${courseId}/upload-syllabus`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success(response.data.message);
      setShowUploadModal(false);
      loadCourseData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload syllabus');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteCourse = async () => {
    if (!confirm('Are you sure you want to delete this course? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/study-buddy/courses/${courseId}`);
      toast.success('Course deleted successfully');
      navigate('/study-buddy');
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  // Convert syllabus items AND study sessions to calendar events
  const getCalendarEvents = () => {
    const events = [];
    
    // Add syllabus items
    if (syllabusData?.items) {
      syllabusData.items
        .filter(item => filters[item.item_type] !== false)
        .forEach(item => {
          let eventDate;
          try {
            eventDate = moment(item.due_date).toDate();
          } catch (e) {
            eventDate = new Date();
          }
          
          events.push({
            id: item.item_id,
            title: item.title,
            start: eventDate,
            end: eventDate,
            allDay: true,
            resource: { ...item, source: 'syllabus' }
          });
        });
    }
    
    // Add S.M.A.R.T. goal study sessions
    if (smartGoals && filters.study_session) {
      smartGoals.forEach(goal => {
        if (goal.calendar_events) {
          goal.calendar_events.forEach(session => {
            let sessionDate;
            try {
              sessionDate = moment(session.date).toDate();
            } catch (e) {
              sessionDate = new Date();
            }
            
            events.push({
              id: `goal-${goal.id}-${session.date}`,
              title: `📚 ${session.title}`,
              start: sessionDate,
              end: sessionDate,
              allDay: true,
              resource: {
                ...session,
                item_type: 'study_session',
                source: 'smart_goal',
                goal_id: goal.id,
                goal_summary: goal.goal_summary
              }
            });
          });
        }
      });
    }
    
    // Add manual events
    if (manualEvents && manualEvents.length > 0) {
      manualEvents
        .filter(event => filters[event.event_type] !== false)
        .forEach(event => {
          let eventDate;
          try {
            eventDate = moment(event.due_date).toDate();
          } catch (e) {
            eventDate = new Date();
          }
          
          events.push({
            id: `manual-${event.id}`,
            title: `✏️ ${event.title}`,
            start: eventDate,
            end: eventDate,
            allDay: true,
            resource: {
              ...event,
              item_type: event.event_type,
              source: 'manual'
            }
          });
        });
    }
    
    return events;
  };

  // Get event style based on type
  const eventStyleGetter = (event) => {
    const item = event.resource;
    const colors = {
      exam: { backgroundColor: '#ef4444', borderColor: '#dc2626' },
      assignment: { backgroundColor: '#10b981', borderColor: '#059669' },
      project: { backgroundColor: '#3b82f6', borderColor: '#2563eb' },
      reading: { backgroundColor: '#f59e0b', borderColor: '#d97706' },
      quiz: { backgroundColor: '#8b5cf6', borderColor: '#7c3aed' },
      study_session: { backgroundColor: '#E8934D', borderColor: '#E85D4D' }, // Orange for study sessions
      other: { backgroundColor: '#6b7280', borderColor: '#4b5563' }
    };
    
    const style = colors[item.item_type] || colors.other;
    
    return {
      style: {
        ...style,
        color: 'white',
        borderRadius: '4px',
        border: 'none',
        display: 'block',
        fontSize: '0.875rem',
        fontWeight: '600'
      }
    };
  };

  const handleSelectEvent = (event) => {
    setSelectedItem(event.resource);
    setShowItemModal(true);
  };
  
  const handleEditItem = (item, index) => {
    setSelectedItem(item);
    setSelectedItemIndex(index);
    setShowEditModal(true);
    setShowItemModal(false);
  };
  
  const handleSaveItem = async (itemIndex, updatedData) => {
    try {
      const response = await axios.put(
        `${API}/study-buddy/courses/${courseId}/syllabus/items/${itemIndex}`,
        updatedData,
        { withCredentials: true }
      );
      
      // Update local state with new syllabus data
      setSyllabusData(response.data);
      
      // Show success message with calendar info
      if (updatedData.due_date && moment(updatedData.due_date).isValid()) {
        toast.success('✓ Task updated and added to calendar!');
      } else {
        toast.success('✓ Task updated successfully!');
      }
      
      setShowEditModal(false);
    } catch (error) {
      console.error('Failed to update item:', error);
      throw error; // Re-throw to let modal handle it
    }
  };

  const toggleFilter = (type) => {
    setFilters(prev => ({ ...prev, [type]: !prev[type] }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E8934D]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/study-buddy')}
              className="text-[#2C1810] hover:text-[#E85D4D] font-semibold mb-4 flex items-center gap-2"
            >
              ← Back to Study Buddy
            </button>
            
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-4xl font-bold retro-title text-[#2C1810] mb-2">
                  {course.course_name}
                </h1>
                <p className="text-xl text-gray-700">{course.school_name}</p>
                {course.course_code && (
                  <p className="text-gray-600 mt-1">{course.course_code}</p>
                )}
                {course.instructor_name && (
                  <p className="text-gray-600">Instructor: {course.instructor_name}</p>
                )}
                {course.semester && (
                  <p className="text-gray-600">{course.semester}</p>
                )}
              </div>
              
              <button
                onClick={handleDeleteCourse}
                className="p-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                title="Delete Course"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Syllabus Section */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Upload Section */}
            <div className="lg:col-span-1">
              <div className="bg-white border-4 border-[#E8934D] rounded-lg shadow-lg p-6">
                <h2 className="text-2xl font-bold text-[#2C1810] mb-4 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-[#E8934D]" />
                  Syllabus
                </h2>

                {!course.syllabus_processed ? (
                  <div className="text-center py-8">
                    <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">No syllabus uploaded yet</p>
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="px-6 py-3 bg-[#E8934D] text-white font-bold rounded-lg hover:bg-[#E85D4D] transition-colors flex items-center gap-2 mx-auto"
                    >
                      <Upload className="w-5 h-5" />
                      Upload Syllabus
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 mb-4">
                      <p className="text-green-800 font-bold flex items-center gap-2">
                        ✓ Syllabus Processed
                      </p>
                      <p className="text-sm text-green-700 mt-1">
                        {syllabusData?.items?.length || 0} items extracted
                      </p>
                    </div>
                    
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="w-full px-4 py-2 border-2 border-[#E8934D] text-[#E8934D] font-bold rounded-lg hover:bg-[#E8934D] hover:text-white transition-colors"
                    >
                      Re-upload Syllabus
                    </button>
                  </div>
                )}
              </div>
              
              {/* S.M.A.R.T. Goals Section */}
              {smartGoals && smartGoals.length > 0 && (
                <div className="bg-white border-4 border-[#E8934D] rounded-lg shadow-lg p-6 mt-6">
                  <h2 className="text-2xl font-bold text-[#2C1810] mb-4 flex items-center gap-2">
                    <Target className="w-6 h-6 text-[#E8934D]" />
                    S.M.A.R.T. Goals
                  </h2>
                  <div className="space-y-3">
                    {smartGoals.map(goal => (
                      <div
                        key={goal.id}
                        onClick={() => navigate(`/study-buddy/smart-goal/${goal.id}`)}
                        className="bg-white border-2 border-gray-300 rounded-lg p-4 hover:border-[#E8934D] transition-colors cursor-pointer"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <p className="font-bold text-[#2C1810] flex-1">{goal.goal_summary}</p>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span>{goal.progress_percentage}% complete</span>
                          <span>•</span>
                          <span>{goal.total_hours_required}hrs total</span>
                        </div>
                        <div className="mt-2">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-500 h-2 rounded-full"
                              style={{ width: `${goal.progress_percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Calendar/Items Section */}
            <div className="lg:col-span-2">
              <div className="bg-white border-4 border-[#E8934D] rounded-lg shadow-lg p-6">
                {/* Header with view toggle */}
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <h2 className="text-2xl font-bold text-[#2C1810] flex items-center gap-2">
                    <CalendarIcon className="w-6 h-6 text-[#E8934D]" />
                    Course Schedule
                  </h2>
                  
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => setShowAddEventModal(true)}
                      className="px-4 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Event
                    </button>
                    
                    {syllabusData?.items && syllabusData.items.length > 0 && (
                      <>
                        <button
                          onClick={() => setViewMode('calendar')}
                          className={`px-4 py-2 rounded-lg font-bold transition-colors ${
                            viewMode === 'calendar'
                              ? 'bg-[#E8934D] text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          Calendar
                        </button>
                        <button
                          onClick={() => setViewMode('list')}
                          className={`px-4 py-2 rounded-lg font-bold transition-colors ${
                            viewMode === 'list'
                              ? 'bg-[#E8934D] text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          List
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Filters */}
                {syllabusData?.items && syllabusData.items.length > 0 && (
                  <div className="mb-4 flex flex-wrap gap-2">
                    <span className="text-sm font-bold text-gray-700 flex items-center gap-2">
                      <Filter className="w-4 h-4" />
                      Filter:
                    </span>
                    {[
                      { key: 'exam', label: 'Exams', color: 'bg-red-500' },
                      { key: 'assignment', label: 'Assignments', color: 'bg-green-500' },
                      { key: 'project', label: 'Projects', color: 'bg-blue-500' },
                      { key: 'quiz', label: 'Quizzes', color: 'bg-purple-500' },
                      { key: 'reading', label: 'Readings', color: 'bg-yellow-500' },
                      { key: 'study_session', label: '📚 Study Sessions', color: 'bg-[#E8934D]' },
                      { key: 'other', label: 'Other', color: 'bg-gray-500' }
                    ].map(({ key, label, color }) => (
                      <button
                        key={key}
                        onClick={() => toggleFilter(key)}
                        className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                          filters[key]
                            ? `${color} text-white`
                            : 'bg-gray-200 text-gray-400'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}

                {syllabusData?.items && syllabusData.items.length > 0 ? (
                  viewMode === 'calendar' ? (
                    /* Calendar View */
                    <div className="bg-white rounded-lg p-4" style={{ height: '600px' }}>
                      <Calendar
                        localizer={localizer}
                        events={getCalendarEvents()}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: '100%' }}
                        onSelectEvent={handleSelectEvent}
                        eventPropGetter={eventStyleGetter}
                        views={['month', 'week', 'agenda']}
                        date={currentDate}
                        onNavigate={(date) => setCurrentDate(date)}
                        view={currentView}
                        onView={(view) => setCurrentView(view)}
                      />
                    </div>
                  ) : (
                    /* List View */
                    <div className="space-y-3">
                      {syllabusData.items
                        .filter(item => filters[item.item_type] !== false)
                        .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
                        .map((item, index) => (
                          <div
                            key={index}
                            className="bg-white border-2 border-gray-300 rounded-lg p-4 hover:border-[#E8934D] transition-colors"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div 
                                className="flex-1 cursor-pointer"
                                onClick={() => {
                                  setSelectedItem(item);
                                  setSelectedItemIndex(index);
                                  setShowItemModal(true);
                                }}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`px-2 py-1 text-xs font-bold rounded ${
                                    item.item_type === 'exam' ? 'bg-red-500 text-white' :
                                    item.item_type === 'assignment' ? 'bg-green-500 text-white' :
                                    item.item_type === 'project' ? 'bg-blue-500 text-white' :
                                    item.item_type === 'quiz' ? 'bg-purple-500 text-white' :
                                    item.item_type === 'reading' ? 'bg-yellow-500 text-white' :
                                    'bg-gray-500 text-white'
                                  }`}>
                                    {item.item_type?.toUpperCase()}
                                  </span>
                                  {item.weight_percentage && (
                                    <span className="text-sm text-gray-600">
                                      {item.weight_percentage}%
                                    </span>
                                  )}
                                </div>
                                <h3 className="font-bold text-[#2C1810]">{item.title}</h3>
                                {item.description && (
                                  <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                                )}
                                {item.due_date && moment(item.due_date).isValid() ? (
                                  <p className="text-sm text-[#E85D4D] font-bold mt-2">
                                    Due: {moment(item.due_date).format('MMM D, YYYY')}
                                  </p>
                                ) : (
                                  <p className="text-sm text-red-600 font-bold mt-2">
                                    Due: Invalid date
                                  </p>
                                )}
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditItem(item, index);
                                }}
                                className="p-2 text-[#E8934D] hover:bg-[#E8934D] hover:text-white rounded-lg transition-colors flex-shrink-0"
                                title="Edit task"
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  )
                ) : (
                  <div className="text-center py-12">
                    <CalendarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">
                      {course.syllabus_processed
                        ? 'No schedule items found in syllabus'
                        : 'Upload a syllabus to see your course schedule'}
                    </p>
                  </div>
                )}
              </div>

              {/* Grading Breakdown */}
              {syllabusData?.grading_breakdown && Object.keys(syllabusData.grading_breakdown).length > 0 && (
                <div className="bg-white border-4 border-[#E8934D] rounded-lg shadow-lg p-6 mt-6">
                  <h2 className="text-2xl font-bold text-[#2C1810] mb-4">Grading Breakdown</h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {Object.entries(syllabusData.grading_breakdown).map(([category, percentage]) => (
                      <div key={category} className="bg-white border-2 border-gray-300 rounded-lg p-4">
                        <p className="text-sm text-gray-600">{category}</p>
                        <p className="text-2xl font-bold text-[#E8934D]">{percentage}%</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4">
          <div className="bg-gradient-to-br from-[#3D2817] via-[#2C1810] to-[#3D2817] max-w-lg w-full p-8 relative rounded-lg shadow-2xl border-4 border-[#E8B67A]">
            <button
              onClick={() => !uploading && setShowUploadModal(false)}
              className="absolute top-4 right-4 text-white hover:text-[#E8B67A] text-2xl font-bold"
              disabled={uploading}
            >
              ×
            </button>

            <h2 className="text-3xl retro-title text-white mb-6">Upload Syllabus</h2>

            <div className="mb-6">
              <label className="block w-full">
                <div className="border-4 border-dashed border-[#E8B67A] rounded-lg p-8 text-center cursor-pointer hover:border-[#E8934D] transition-colors bg-white/5">
                  <Upload className="w-12 h-12 text-[#E8B67A] mx-auto mb-4" />
                  <p className="text-white font-bold mb-2">Click to upload PDF</p>
                  <p className="text-sm text-gray-400">Maximum file size: 10MB</p>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="hidden"
                  />
                </div>
              </label>
            </div>

            {uploading && (
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E8B67A] mx-auto mb-4"></div>
                <p className="text-white">Processing syllabus with AI...</p>
                <p className="text-sm text-gray-400 mt-2">This may take a few moments</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Item Detail Modal */}
      {showItemModal && selectedItem && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4">
          <div className="bg-white max-w-2xl w-full p-8 relative rounded-lg shadow-2xl">
            <button
              onClick={() => setShowItemModal(false)}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-2xl font-bold"
            >
              ×
            </button>

            <div className="mb-4">
              <span className={`px-3 py-1 text-sm font-bold rounded ${
                selectedItem.item_type === 'study_session' ? 'bg-[#E8934D] text-white' :
                selectedItem.item_type === 'exam' ? 'bg-red-500 text-white' :
                selectedItem.item_type === 'assignment' ? 'bg-green-500 text-white' :
                selectedItem.item_type === 'project' ? 'bg-blue-500 text-white' :
                selectedItem.item_type === 'quiz' ? 'bg-purple-500 text-white' :
                selectedItem.item_type === 'reading' ? 'bg-yellow-500 text-white' :
                'bg-gray-500 text-white'
              }`}>
                {selectedItem.item_type === 'study_session' ? 'STUDY SESSION' : selectedItem.item_type?.toUpperCase()}
              </span>
            </div>

            <h2 className="text-3xl font-bold text-[#2C1810] mb-4">
              {selectedItem.title}
            </h2>

            {selectedItem.due_date && (
              <div className="mb-4 p-4 bg-[#E85D4D]/10 border-2 border-[#E85D4D] rounded-lg">
                <p className="text-sm text-gray-700 mb-1">Due Date</p>
                <p className="text-xl font-bold text-[#E85D4D]">
                  {moment(selectedItem.due_date).format('MMMM D, YYYY')}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {moment(selectedItem.due_date).fromNow()}
                </p>
              </div>
            )}

            {(selectedItem.weight_percentage || selectedItem.point_value) && (
              <div className="grid grid-cols-2 gap-4 mb-4">
                {selectedItem.weight_percentage && (
                  <div className="p-4 bg-gray-100 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Weight</p>
                    <p className="text-2xl font-bold text-[#2C1810]">
                      {selectedItem.weight_percentage}%
                    </p>
                  </div>
                )}
                {selectedItem.point_value && (
                  <div className="p-4 bg-gray-100 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Points</p>
                    <p className="text-2xl font-bold text-[#2C1810]">
                      {selectedItem.point_value}
                    </p>
                  </div>
                )}
              </div>
            )}

            {selectedItem.description && (
              <div className="mb-6">
                <h3 className="text-lg font-bold text-[#2C1810] mb-2">Description</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedItem.description}</p>
              </div>
            )}

            {selectedItem.requirements && selectedItem.requirements.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-bold text-[#2C1810] mb-2">Requirements</h3>
                <ul className="list-disc list-inside space-y-1">
                  {selectedItem.requirements.map((req, idx) => (
                    <li key={idx} className="text-gray-700">{req}</li>
                  ))}
                </ul>
              </div>
            )}

            {selectedItem.submission_format && (
              <div className="mb-6">
                <h3 className="text-lg font-bold text-[#2C1810] mb-2">Submission Format</h3>
                <p className="text-gray-700">{selectedItem.submission_format}</p>
              </div>
            )}

            <div className="flex gap-4 pt-4 border-t">
              {selectedItem.source !== 'manual' && (
                <button
                  onClick={() => handleEditItem(selectedItem, selectedItemIndex)}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-[#E8934D] to-[#E85D4D] text-white font-bold rounded-lg hover:shadow-lg transition-all flex items-center justify-center gap-2"
                >
                  <Edit className="w-5 h-5" />
                  Edit Task
                </button>
              )}
              <button
                onClick={() => setShowItemModal(false)}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 font-bold rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
              
              {selectedItem.source === 'smart_goal' ? (
                <button
                  onClick={() => {
                    setShowItemModal(false);
                    navigate(`/study-buddy/smart-goal/${selectedItem.goal_id}`);
                  }}
                  className="flex-1 px-6 py-3 bg-[#E8934D] text-white font-bold rounded-lg hover:bg-[#E85D4D] transition-colors"
                >
                  📊 View S.M.A.R.T. Goal
                </button>
              ) : (
                <button
                  onClick={() => {
                    setShowItemModal(false);
                    navigate(`/study-buddy/course/${courseId}/smart-goal/create`, {
                      state: { item: selectedItem, course: course }
                    });
                  }}
                  className="flex-1 px-6 py-3 bg-[#E8934D] text-white font-bold rounded-lg hover:bg-[#E85D4D] transition-colors"
                >
                  🎯 Create S.M.A.R.T. Goal
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Add Manual Event Modal */}
      {showAddEventModal && (
        <AddManualEventModal
          courseId={courseId}
          onClose={() => setShowAddEventModal(false)}
          onSuccess={() => {
            setShowAddEventModal(false);
            loadCourseData();
          }}
        />
      )}
      
      {/* Edit Syllabus Item Modal */}
      {showEditModal && selectedItem && (
        <EditSyllabusItemModal
          item={selectedItem}
          itemIndex={selectedItemIndex}
          courseId={courseId}
          onClose={() => setShowEditModal(false)}
          onSave={handleSaveItem}
        />
      )}
    </Layout>
  );
}
